import React, { useCallback, useMemo, useState } from "react";

import { isAxiosError } from "axios";
import { orderBy } from "lodash";
import { useParams } from "react-router-dom";

import useAllAccounts from "@fartherfinance/frontend/api/Accounts/hooks/useAllAccounts";
import {
  GoalBasedFinancialPlanId,
  GoalBasedFinancialPlanIncomeId,
} from "@fartherfinance/frontend/api/FinancialPlans/ids";
import useDeleteGoalBasedFinancialPlanIncome from "@fartherfinance/frontend/api/FinancialPlans/income/hooks/useDeleteGoalBasedFinancialPlanIncome";
import usePatchGoalBasedFinancialPlanIncome from "@fartherfinance/frontend/api/FinancialPlans/income/hooks/usePatchGoalBasedFinancialPlanIncome";
import usePostGoalBasedFinancialPlanIncome from "@fartherfinance/frontend/api/FinancialPlans/income/hooks/usePostGoalBasedFinancialPlanIncome";
import {
  GoalBasedFinancialPlanIncomeResponse,
  GoalBasedIncomeType,
} from "@fartherfinance/frontend/api/FinancialPlans/income/types";
import useGetGoalBasedFinancialPlan from "@fartherfinance/frontend/api/FinancialPlans/plan/hooks/useGetGoalBasedFinancialPlan";
import { GoalBasedFinancialPlanRetirementHorizonResponse } from "@fartherfinance/frontend/api/FinancialPlans/retirementHorizon/types";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import { AddedAccountOption } from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Assets/Types";
import AddMoreSection from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/AddMoreSection/AddMoreSection";
import CardsGrid from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/CardsGrid/CardsGrid";
import {
  CreateUpdateFlowScrollContainerDivID,
  defaultPlanDateFieldValues,
  OwnerField,
} from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/constants";
import EmptyCard from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/EmptyCard/EmptyCard";
import FilledCard from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/FilledCard/FilledCard";
import { getDateRangeStringFromPlanDates } from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/helpers/getDateRangeStringFromPlanDates";
import { getInitialPlanDateFieldValuesForForm } from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/helpers/getInitialPlanDateFieldValuesForForm";
import { ClientAndSpouseDetails } from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/hooks/useGetClientAndSpouseDetails";
import {
  AnnualIncreaseAmountField,
  AnnualIncreaseTypeField,
  annualIncreaseTypeOptions,
  CustomInflation,
  defaultAnnualIncreaseFieldValues,
  defaultAnnualIncreaseTypeField,
} from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/MultiDropdown/AnnualIncrease/AnnualIncrease";
import { OwnerFieldType } from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/Types";
import { ErrorMessage } from "@src/multiCustodian/components/TaxBudgets/reducer/types";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import BodyContentFormatter from "@src/sharedComponents/BodyContentFormatter/BodyContentFormatter";
import CustomDropdownBase from "@src/sharedComponents/CustomDropdownBase/CustomDropdownBase";
import MenuOptionsWithCheckIfSelected from "@src/sharedComponents/CustomDropdownBase/MenuOptionsWithCheckIfSelected/MenuOptionsWithCheckIfSelected";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import PageHero from "@src/sharedComponents/PageHero/PageHero";
import TitleBlock from "@src/sharedComponents/PageHero/TitleBlock";
import Text from "@src/sharedComponents/Typography/Text";
import { formatExternalAccountName } from "@src/utils/ExternalAccount/formatExternalAccountName";
import { getExternalAccountName } from "@src/utils/ExternalAccount/getExternalAccountName";
import { pluralize } from "@src/utils/pluralize";
import { scrollElementIdToTopOfPage } from "@src/utils/scrollElementIdToTopOfPage";
import toUSD from "@src/utils/toUSD";

import AnnuityIncomeFormDrawer from "./Forms/Annuity";
import BonusIncomeFormDrawer from "./Forms/Bonus";
import ChildSupportIncomeFormDrawer from "./Forms/ChildSupport";
import DistributionsIncomeFormDrawer from "./Forms/Distributions";
import InheritanceIncomeFormDrawer from "./Forms/Inheritance";
import OtherIncomeFormDrawer from "./Forms/OtherIncome";
import PensionIncomeFormDrawer from "./Forms/Pension";
import ReverseMortgageIncomeFormDrawer from "./Forms/ReverseMortgage";
import RoyaltyIncomeFormDrawer from "./Forms/Royalty";
import SalaryIncomeFormDrawer from "./Forms/Salary";
import SocialSecurityIncomeFormDrawer from "./Forms/SocialSecurity";

import styles from "./Income.module.css";

const sortByCreatedAtAsc = (
  incomeElements: GoalBasedFinancialPlanIncomeResponse[]
): GoalBasedFinancialPlanIncomeResponse[] => {
  return orderBy(incomeElements, [(el) => el.createdAt], ["asc"]);
};

interface IncomeTypeOption {
  id: GoalBasedIncomeType;
  label: string;
}

const incomeTypes: IncomeTypeOption[] = [
  {
    id: "Annuity",
    label: "Annuity",
  },
  {
    id: "Bonus",
    label: "Bonus",
  },
  {
    id: "ChildSupport",
    label: "Child Support",
  },
  {
    id: "Distributions",
    label: "Distributions",
  },
  {
    id: "Inheritance",
    label: "Inheritance",
  },
  {
    id: "OtherIncome",
    label: "Other Income",
  },
  {
    id: "Pension",
    label: "Pension",
  },
  {
    id: "ReverseMortgage",
    label: "Reverse Mortgage",
  },
  {
    id: "Royalty",
    label: "Royalty",
  },
  {
    id: "Salary",
    label: "Salary",
  },
  {
    id: "SocialSecurity",
    label: "Social Security",
  },
] as const;

const getCardSubTitle = (incomeType: GoalBasedIncomeType) => {
  const option = incomeTypes.find((t) => t.id === incomeType);

  if (option === undefined) {
    return "";
  }

  return option.label;
};

interface Props {
  clientSpouseData: ClientAndSpouseDetails;
}

const FinancialPlanIncome: React.FC<Props> = ({ clientSpouseData }) => {
  const { clientId, goalBasedFinancialPlanId } = useParams<{
    clientId: ClientId;
    goalBasedFinancialPlanId: GoalBasedFinancialPlanId;
  }>();

  const [showDropdown, setShowDropdown] = useState<boolean>(false);

  const [goalBasedFinancialPlanIncomeId, setGoalBasedFinancialPlanIncomeId] =
    useState<GoalBasedFinancialPlanIncomeId | undefined>(undefined);

  const [drawer, setDrawer] = useState<GoalBasedIncomeType | undefined>(
    undefined
  );

  const [defaultOwner, setDefaultOwner] = useState<OwnerFieldType | undefined>(
    undefined
  );

  const notify = useStatusNotification();

  const auth = useRequestAuth();

  const financialPlan = useGetGoalBasedFinancialPlan(
    goalBasedFinancialPlanId,
    auth
  );
  const allAccounts = useAllAccounts(clientId, auth);

  const { client, spouse } = clientSpouseData;

  const { firstName: clientFirstName, fullName: clientFullName } = client;

  const spouseFirstName = spouse?.firstName ?? "Spouse";
  const spouseFullName = spouse?.fullName ?? "Spouse";

  const getNameOfClientOrSpouse = useCallback(
    (ownerId: ClientId) => {
      return ownerId === clientId ? clientFullName : spouseFullName;
    },
    [clientId, clientFullName, spouseFullName]
  );

  const ownersOptions = useMemo(() => {
    if (financialPlan.data?.retirementHorizon === undefined) {
      return [];
    }

    return financialPlan.data.retirementHorizon.map((rh) => ({
      label: rh.owner === clientId ? clientFullName : spouseFullName,
      value: rh.owner,
    }));
  }, [
    clientId,
    clientFullName,
    spouseFullName,
    financialPlan.data?.retirementHorizon,
  ]);

  const {
    mutateAsync: deleteGoalBasedFinancialPlanIncome,
    isLoading: isMutatingDelete,
  } = useDeleteGoalBasedFinancialPlanIncome(auth);

  const {
    mutateAsync: patchGoalBasedFinancialPlanIncome,
    isLoading: isMutatingPatch,
  } = usePatchGoalBasedFinancialPlanIncome(auth);

  const {
    mutateAsync: postGoalBasedFinancialPlanIncome,
    isLoading: isMutatingPost,
  } = usePostGoalBasedFinancialPlanIncome(auth);

  const onCloseDrawer = useCallback(() => {
    setDrawer(undefined);
    setDefaultOwner(undefined);
  }, []);

  const onRemoveIncomeSource = useCallback(
    async (incomeSource: string) => {
      if (goalBasedFinancialPlanIncomeId === undefined) {
        return;
      }

      try {
        await deleteGoalBasedFinancialPlanIncome({
          goalBasedFinancialPlanId,
          goalBasedFinancialPlanIncomeId,
        });

        notify(`${incomeSource} removed`, "Success");

        setGoalBasedFinancialPlanIncomeId(undefined);

        onCloseDrawer();
      } catch (error) {
        if (isAxiosError<ErrorMessage>(error)) {
          const message = error.response?.data.message;
          notify(`Error removing ${incomeSource}: ${message ?? ""}`, "Error");
        } else {
          notify(`Error removing ${incomeSource}`, "Error");
        }
      }
    },
    [
      goalBasedFinancialPlanId,
      goalBasedFinancialPlanIncomeId,
      deleteGoalBasedFinancialPlanIncome,
      notify,
      onCloseDrawer,
    ]
  );

  const mutations = useMemo(
    () => ({
      onAddIncomeSource: postGoalBasedFinancialPlanIncome,
      onUpdateIncomeSource: patchGoalBasedFinancialPlanIncome,
      onDeleteIncomeSource: onRemoveIncomeSource,
      isMutating: isMutatingDelete || isMutatingPatch || isMutatingPost,
    }),
    [
      postGoalBasedFinancialPlanIncome,
      patchGoalBasedFinancialPlanIncome,
      onRemoveIncomeSource,
      isMutatingDelete,
      isMutatingPatch,
      isMutatingPost,
    ]
  );

  const addedAccountOptions: AddedAccountOption[] = useMemo(() => {
    if (financialPlan.isLoading || financialPlan.hasError) {
      return [];
    }

    if (allAccounts.isLoading || allAccounts.hasError) {
      return [];
    }

    return (financialPlan.data?.accounts ?? []).map((acc) => {
      let label = undefined;
      if (acc.accountType === "FartherManagedAccount") {
        const account = (allAccounts.data.fartherAccounts ?? []).find(
          (el) => el.virtualAccountId === acc.accountId
        );
        if (account === undefined) {
          throw new Error(`Managed account: ${acc.accountId} not found`);
        }

        label = account.accountDetails.displayName;
      } else if (acc.accountType === "ManuallyTrackedAccount") {
        const account = (allAccounts.data.manuallyTrackedAccounts ?? []).find(
          (el) => el.id === acc.accountId
        );
        if (account === undefined) {
          throw new Error(`Manual account: ${acc.accountId} not found`);
        }

        label = account.assetName;
      } else {
        const account = (allAccounts.data.externalAccounts ?? []).find(
          (el) => el.accountId === acc.accountId
        );
        if (account === undefined) {
          throw new Error(`External account: ${acc.accountId} not found`);
        }

        label = formatExternalAccountName(getExternalAccountName(account), 14);
      }

      return {
        label,
        value: acc.accountId,
      };
    });
  }, [financialPlan, allAccounts]);

  const onSelectDropdownOption = useCallback(
    (el: IncomeTypeOption) => {
      setGoalBasedFinancialPlanIncomeId(undefined);
      setDrawer(el.id);
      setShowDropdown(false);
    },
    [setDrawer, setShowDropdown]
  );

  const existingIncomeElement = useMemo(() => {
    if (goalBasedFinancialPlanIncomeId === undefined) {
      return undefined;
    }

    return (financialPlan.data?.income ?? []).find(
      (i) => i.incomeId === goalBasedFinancialPlanIncomeId
    );
  }, [financialPlan.data, goalBasedFinancialPlanIncomeId]);

  const initialPlanDateOwnerAndAnnualIncreaseFields = useMemo(() => {
    let ownerField = { label: clientFullName, value: clientId };

    if (existingIncomeElement === undefined) {
      return {
        [OwnerField]: defaultOwner ? defaultOwner : ownerField,
        ...defaultAnnualIncreaseFieldValues,
        ...defaultPlanDateFieldValues,
      };
    }

    if (financialPlan.data === undefined) {
      return undefined;
    }

    if (
      existingIncomeElement?.owner !== undefined &&
      existingIncomeElement.owner !== clientId
    ) {
      ownerField = {
        label: spouseFullName,
        value: existingIncomeElement.owner,
      };
    }

    const annualIncreaseTypeField = annualIncreaseTypeOptions.find(
      (el) => el.value === existingIncomeElement?.annualIncrease?.type
    ) ?? { ...defaultAnnualIncreaseTypeField };

    const annualIncreaseAmountField =
      existingIncomeElement?.annualIncrease?.type === CustomInflation
        ? existingIncomeElement?.annualIncrease?.value.toString()
        : undefined;

    return {
      ...getInitialPlanDateFieldValuesForForm({
        clientId,
        retirementHorizons: financialPlan.data.retirementHorizon,
        existingCategoryElement: existingIncomeElement,
      }),
      [OwnerField]: ownerField,
      [AnnualIncreaseTypeField]: annualIncreaseTypeField,
      [AnnualIncreaseAmountField]: annualIncreaseAmountField,
    };
  }, [
    clientId,
    financialPlan,
    existingIncomeElement,
    clientFullName,
    spouseFullName,
    defaultOwner,
  ]);

  const getCardHeading = useCallback(
    (incomeType: GoalBasedIncomeType, ownerId: ClientId) => {
      if (client.firstName === undefined) {
        return "";
      }

      const incomeTypeLabel = incomeTypes.find(
        (t) => t.id === incomeType
      )?.label;
      if (incomeTypeLabel === undefined) {
        return "";
      }

      const ownerFirstName =
        ownerId === clientId ? clientFirstName : spouseFirstName;

      return `${ownerFirstName}'s ${incomeTypeLabel}`;
    },
    [clientId, client, clientFirstName, spouseFirstName]
  );

  const onClickEmptyCard = useCallback(
    (ownerId: ClientId, incomeType: GoalBasedIncomeType | undefined) => {
      setDefaultOwner({
        label: ownerId === clientId ? clientFullName : spouseFullName,
        value: ownerId,
      });
      setGoalBasedFinancialPlanIncomeId(undefined);
      setDrawer(incomeType);
    },
    [clientId, clientFullName, spouseFullName]
  );

  const spouseInPlan = useMemo(() => {
    return (financialPlan.data?.retirementHorizon ?? []).some(
      (r) => r.owner !== clientId
    );
  }, [clientId, financialPlan.data]);

  const onScrollToTopAndShowDropdown = () => {
    scrollElementIdToTopOfPage(CreateUpdateFlowScrollContainerDivID);
    setShowDropdown(true);
  };

  const sortClientRetirementHorizonFirst = useCallback(
    (
      acc: GoalBasedFinancialPlanRetirementHorizonResponse[],
      cur: GoalBasedFinancialPlanRetirementHorizonResponse
    ) => {
      if (cur.owner === clientId) {
        acc.unshift(cur); // client comes first
      } else {
        acc.push(cur); // spouse/non-client comes after client
      }

      return acc;
    },
    [clientId]
  );

  return (
    <>
      {financialPlan.isLoading && <LogoLoadingStill onTop={true} />}

      <PageHero
        className={styles.pageHero}
        innerClassName={styles.pageHeroInner}
        start={
          <TitleBlock
            title="Income"
            subTitle="Include both recurring income, like a salary, and one-time income, like the sale of a home."
          />
        }
        end={
          <CustomDropdownBase
            className={styles.dropdownBase}
            curItem={{
              value: "Add Income",
              displayComponent: (
                <Text variant={"body"} color={"text"}>
                  Add Income
                </Text>
              ),
            }}
            showDropdown={showDropdown}
            setShowDropdown={setShowDropdown}
            width={240}
            isDisabled={false}
            hideIcon={true}
          >
            <MenuOptionsWithCheckIfSelected
              onSelect={onSelectDropdownOption}
              curItemName={"Add Income"}
              options={incomeTypes}
              maxHeight={515}
            />
          </CustomDropdownBase>
        }
        endClassName={styles.pageHeroEnd}
      />

      <BodyContentFormatter>
        <div className={styles.contentContainer}>
          <div className={styles.takeUpSpace4} />

          <div className={styles.heading}>
            <Text variant={"heading1"} color={"text"}>
              Salaries
            </Text>

            <Text variant={"body"} color={"textSubtle"}>
              {pluralize(
                (financialPlan.data?.income ?? []).filter(
                  (i) => i.incomeType === "Salary"
                ).length,
                "Salary",
                true
              )}
            </Text>
          </div>

          <CardsGrid>
            {sortByCreatedAtAsc(financialPlan.data?.income ?? [])
              .filter((i) => i.incomeType === "Salary")
              .map((existingSalary) => {
                return (
                  <FilledCard
                    key={existingSalary.owner}
                    className={styles.filledCard}
                    title={toUSD(parseFloat(existingSalary.amount), {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                    subTitle={getCardSubTitle(existingSalary.incomeType)}
                    titleRight={getDateRangeStringFromPlanDates({
                      startDate: existingSalary.startDate,
                      endDate: existingSalary.endDate,
                      clientAndSpouseDetails: clientSpouseData,
                      repeats: existingSalary.repeats,
                      owner: existingSalary.owner,
                    })}
                    heading={getCardHeading(
                      existingSalary.incomeType,
                      existingSalary.owner
                    )}
                    text={getNameOfClientOrSpouse(existingSalary.owner)}
                    onClick={() => {
                      setGoalBasedFinancialPlanIncomeId(
                        existingSalary.incomeId
                      );
                      setDrawer(existingSalary.incomeType);
                    }}
                  />
                );
              })}

            {(financialPlan.data?.retirementHorizon ?? [])
              .filter(
                (rh) =>
                  !(financialPlan.data?.income ?? []).find(
                    (i) => i.incomeType === "Salary" && i.owner === rh.owner
                  )
              )
              .reduce(
                sortClientRetirementHorizonFirst,
                [] as GoalBasedFinancialPlanRetirementHorizonResponse[]
              )
              .map((rh) => {
                return (
                  <EmptyCard
                    key={rh.owner}
                    className={styles.emptyCard}
                    onAdd={() => onClickEmptyCard(rh.owner, "Salary")}
                    heading={getCardHeading("Salary", rh.owner)}
                    text={
                      rh.owner === clientId ? clientFullName : spouseFullName
                    }
                  />
                );
              })}
          </CardsGrid>

          <div className={styles.heading}>
            <Text variant={"heading1"} color={"text"}>
              Social Security
            </Text>

            <Text variant={"body"} color={"textSubtle"}>
              {pluralize(
                (financialPlan.data?.income ?? []).filter(
                  (i) => i.incomeType === "SocialSecurity"
                ).length,
                "Source",
                true
              )}
            </Text>
          </div>

          <CardsGrid>
            {sortByCreatedAtAsc(financialPlan.data?.income ?? [])
              .filter((i) => i.incomeType === "SocialSecurity")
              .map((existingSocialSecurity) => {
                return (
                  <FilledCard
                    key={existingSocialSecurity.owner}
                    className={styles.filledCard}
                    title={toUSD(parseFloat(existingSocialSecurity.amount), {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                    subTitle={getCardSubTitle(
                      existingSocialSecurity.incomeType
                    )}
                    titleRight={getDateRangeStringFromPlanDates({
                      startDate: existingSocialSecurity.startDate,
                      endDate: existingSocialSecurity.endDate,
                      clientAndSpouseDetails: clientSpouseData,
                      repeats: existingSocialSecurity.repeats,
                      owner: existingSocialSecurity.owner,
                    })}
                    heading={getCardHeading(
                      existingSocialSecurity.incomeType,
                      existingSocialSecurity.owner
                    )}
                    text={getNameOfClientOrSpouse(existingSocialSecurity.owner)}
                    onClick={() => {
                      setGoalBasedFinancialPlanIncomeId(
                        existingSocialSecurity.incomeId
                      );
                      setDrawer(existingSocialSecurity.incomeType);
                    }}
                  />
                );
              })}

            {(financialPlan.data?.retirementHorizon ?? [])
              .filter(
                (rh) =>
                  !(financialPlan.data?.income ?? []).find(
                    (i) =>
                      i.incomeType === "SocialSecurity" && i.owner === rh.owner
                  )
              )
              .reduce(
                sortClientRetirementHorizonFirst,
                [] as GoalBasedFinancialPlanRetirementHorizonResponse[]
              )
              .map((rh) => {
                return (
                  <EmptyCard
                    key={rh.owner}
                    className={styles.emptyCard}
                    onAdd={() => onClickEmptyCard(rh.owner, "SocialSecurity")}
                    heading={getCardHeading("SocialSecurity", rh.owner)}
                    text={getNameOfClientOrSpouse(rh.owner)}
                  />
                );
              })}
          </CardsGrid>

          {(financialPlan.data?.income ?? []).filter(
            (i) =>
              i.incomeType !== "Salary" && i.incomeType !== "SocialSecurity"
          ).length >= 1 ? (
            <div className={styles.heading}>
              <Text variant={"heading1"} color={"text"}>
                Other Income
              </Text>

              <Text variant={"body"} color={"textSubtle"}>
                {pluralize(
                  (financialPlan.data?.income ?? []).filter(
                    (i) =>
                      i.incomeType !== "Salary" &&
                      i.incomeType !== "SocialSecurity"
                  ).length,
                  "Income Source",
                  true
                )}
              </Text>
            </div>
          ) : (
            <AddMoreSection
              onClick={onScrollToTopAndShowDropdown}
              heading={"Other Income"}
              text={
                "Add other income sources like salaries, pensions, distributions from retirement accounts and more."
              }
            />
          )}

          <CardsGrid>
            {sortByCreatedAtAsc(financialPlan.data?.income ?? [])
              .filter(
                (i) =>
                  i.incomeType !== "Salary" && i.incomeType !== "SocialSecurity"
              )
              .map((i) => {
                return (
                  <FilledCard
                    key={i.incomeId}
                    className={styles.filledCard}
                    title={toUSD(parseFloat(i.amount), {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                    subTitle={getCardSubTitle(i.incomeType)}
                    titleRight={getDateRangeStringFromPlanDates({
                      startDate: i.startDate,
                      endDate: i.endDate,
                      clientAndSpouseDetails: clientSpouseData,
                      repeats: i.repeats,
                      owner: i.owner,
                    })}
                    heading={getCardHeading(i.incomeType, i.owner)}
                    text={getNameOfClientOrSpouse(i.owner)}
                    onClick={() => {
                      setGoalBasedFinancialPlanIncomeId(i.incomeId);
                      setDrawer(i.incomeType);
                    }}
                  />
                );
              })}
          </CardsGrid>

          {initialPlanDateOwnerAndAnnualIncreaseFields !== undefined && (
            <>
              <AnnuityIncomeFormDrawer
                drawerOpen={drawer === "Annuity"}
                onClose={onCloseDrawer}
                goalBasedFinancialPlanIncomeId={goalBasedFinancialPlanIncomeId}
                existingIncomeElement={existingIncomeElement}
                retirementHorizons={financialPlan.data?.retirementHorizon}
                initialPlanDateOwnerAndAnnualIncreaseFields={
                  initialPlanDateOwnerAndAnnualIncreaseFields
                }
                addedAccountOptions={addedAccountOptions}
                mutations={mutations}
                ownersOptions={ownersOptions}
                hideSpouseOptions={!spouseInPlan}
              />

              <BonusIncomeFormDrawer
                drawerOpen={drawer === "Bonus"}
                onClose={onCloseDrawer}
                goalBasedFinancialPlanIncomeId={goalBasedFinancialPlanIncomeId}
                existingIncomeElement={existingIncomeElement}
                retirementHorizons={financialPlan.data?.retirementHorizon}
                initialPlanDateOwnerAndAnnualIncreaseFields={
                  initialPlanDateOwnerAndAnnualIncreaseFields
                }
                addedAccountOptions={addedAccountOptions}
                mutations={mutations}
                ownersOptions={ownersOptions}
                hideSpouseOptions={!spouseInPlan}
              />

              <ChildSupportIncomeFormDrawer
                drawerOpen={drawer === "ChildSupport"}
                onClose={onCloseDrawer}
                goalBasedFinancialPlanIncomeId={goalBasedFinancialPlanIncomeId}
                existingIncomeElement={existingIncomeElement}
                retirementHorizons={financialPlan.data?.retirementHorizon}
                initialPlanDateOwnerAndAnnualIncreaseFields={
                  initialPlanDateOwnerAndAnnualIncreaseFields
                }
                addedAccountOptions={addedAccountOptions}
                mutations={mutations}
                ownersOptions={ownersOptions}
                hideSpouseOptions={!spouseInPlan}
              />

              <DistributionsIncomeFormDrawer
                drawerOpen={drawer === "Distributions"}
                onClose={onCloseDrawer}
                goalBasedFinancialPlanIncomeId={goalBasedFinancialPlanIncomeId}
                existingIncomeElement={existingIncomeElement}
                retirementHorizons={financialPlan.data?.retirementHorizon}
                initialPlanDateOwnerAndAnnualIncreaseFields={
                  initialPlanDateOwnerAndAnnualIncreaseFields
                }
                addedAccountOptions={addedAccountOptions}
                mutations={mutations}
                ownersOptions={ownersOptions}
                hideSpouseOptions={!spouseInPlan}
              />

              <InheritanceIncomeFormDrawer
                drawerOpen={drawer === "Inheritance"}
                onClose={onCloseDrawer}
                goalBasedFinancialPlanIncomeId={goalBasedFinancialPlanIncomeId}
                existingIncomeElement={existingIncomeElement}
                retirementHorizons={financialPlan.data?.retirementHorizon}
                initialPlanDateOwnerAndAnnualIncreaseFields={
                  initialPlanDateOwnerAndAnnualIncreaseFields
                }
                addedAccountOptions={addedAccountOptions}
                mutations={mutations}
                ownersOptions={ownersOptions}
                hideSpouseOptions={!spouseInPlan}
              />

              <OtherIncomeFormDrawer
                drawerOpen={drawer === "OtherIncome"}
                onClose={onCloseDrawer}
                goalBasedFinancialPlanIncomeId={goalBasedFinancialPlanIncomeId}
                existingIncomeElement={existingIncomeElement}
                retirementHorizons={financialPlan.data?.retirementHorizon}
                initialPlanDateOwnerAndAnnualIncreaseFields={
                  initialPlanDateOwnerAndAnnualIncreaseFields
                }
                addedAccountOptions={addedAccountOptions}
                mutations={mutations}
                ownersOptions={ownersOptions}
                hideSpouseOptions={!spouseInPlan}
              />

              <PensionIncomeFormDrawer
                drawerOpen={drawer === "Pension"}
                onClose={onCloseDrawer}
                goalBasedFinancialPlanIncomeId={goalBasedFinancialPlanIncomeId}
                existingIncomeElement={existingIncomeElement}
                retirementHorizons={financialPlan.data?.retirementHorizon}
                initialPlanDateOwnerAndAnnualIncreaseFields={
                  initialPlanDateOwnerAndAnnualIncreaseFields
                }
                addedAccountOptions={addedAccountOptions}
                mutations={mutations}
                ownersOptions={ownersOptions}
                hideSpouseOptions={!spouseInPlan}
              />

              <ReverseMortgageIncomeFormDrawer
                drawerOpen={drawer === "ReverseMortgage"}
                onClose={onCloseDrawer}
                goalBasedFinancialPlanIncomeId={goalBasedFinancialPlanIncomeId}
                existingIncomeElement={existingIncomeElement}
                retirementHorizons={financialPlan.data?.retirementHorizon}
                initialPlanDateOwnerAndAnnualIncreaseFields={
                  initialPlanDateOwnerAndAnnualIncreaseFields
                }
                addedAccountOptions={addedAccountOptions}
                mutations={mutations}
                ownersOptions={ownersOptions}
                hideSpouseOptions={!spouseInPlan}
              />

              <RoyaltyIncomeFormDrawer
                drawerOpen={drawer === "Royalty"}
                onClose={onCloseDrawer}
                goalBasedFinancialPlanIncomeId={goalBasedFinancialPlanIncomeId}
                existingIncomeElement={existingIncomeElement}
                retirementHorizons={financialPlan.data?.retirementHorizon}
                initialPlanDateOwnerAndAnnualIncreaseFields={
                  initialPlanDateOwnerAndAnnualIncreaseFields
                }
                addedAccountOptions={addedAccountOptions}
                mutations={mutations}
                ownersOptions={ownersOptions}
                hideSpouseOptions={!spouseInPlan}
              />

              <SalaryIncomeFormDrawer
                drawerOpen={drawer === "Salary"}
                onClose={onCloseDrawer}
                goalBasedFinancialPlanIncomeId={goalBasedFinancialPlanIncomeId}
                existingIncomeElement={existingIncomeElement}
                retirementHorizons={financialPlan.data?.retirementHorizon}
                initialPlanDateOwnerAndAnnualIncreaseFields={
                  initialPlanDateOwnerAndAnnualIncreaseFields
                }
                addedAccountOptions={addedAccountOptions}
                mutations={mutations}
                ownersOptions={ownersOptions}
                hideSpouseOptions={!spouseInPlan}
              />

              <SocialSecurityIncomeFormDrawer
                drawerOpen={drawer === "SocialSecurity"}
                onClose={onCloseDrawer}
                goalBasedFinancialPlanIncomeId={goalBasedFinancialPlanIncomeId}
                existingIncomeElement={existingIncomeElement}
                retirementHorizons={financialPlan.data?.retirementHorizon}
                initialPlanDateOwnerAndAnnualIncreaseFields={
                  initialPlanDateOwnerAndAnnualIncreaseFields
                }
                addedAccountOptions={addedAccountOptions}
                mutations={mutations}
                ownersOptions={ownersOptions}
                hideSpouseOptions={!spouseInPlan}
              />
            </>
          )}
        </div>
      </BodyContentFormatter>
    </>
  );
};

export default FinancialPlanIncome;
